import {
	BookingData,
	SafeZone,
	BookingSummary,
	ProductsSummary,
	BookingProduct,
	Id,
	Shift,
	Callback,
	Ticket,
	BookingContext,
	PersonalDetailKey,
	ErrorInfo,
	BookingTable,
	Product,
	BookingStep,
	ProductCategory,
	LayoutLocation,
	ValidatePersonalDetailOptions,
	SurchargeData,
	Journeys,
} from '../types'
import { _, save, getZoneName, parseNum, parseDate, removeDuplicates } from './helpers'
import {
	BOOKING_STEPS_IN_ORDER,
	LANG,
	PRODUCTS_WICH_QTY_IS_FIXED_TO_ADULTS,
	PRODUCTS_WICH_QTY_IS_FIXED_TO_CHILDREN,
	PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX,
	SHOW_COUNTRY_FIELD,
} from './symbols'
import { CookieManager } from './cookie-manager'

type Render = { render: () => void }

export class Booking extends CookieManager {
	#booking: BookingData
	#render: Callback<void>
	#hasRender: boolean
	#useLocalStorage: boolean

	constructor(booking: BookingData, useLocalStorage: boolean = false, render?: Callback<void>) {
		super()

		this.#useLocalStorage = useLocalStorage
		this.#booking = JSON.parse(JSON.stringify(booking))
		if (!this.#booking.language) {
			// es-ES -> es
			this.#booking.language = LANG
		}
		//backward compatible
		this.#booking.date = this.#booking.date.split(' ').join('T')

		// TODO: IMPORTANT hardcode journey A
		let journey = 'A' //this.getCookie('cmm_journey')
		if (journey !== 'A' && journey !== 'B') {
			journey = Math.random() > 0.5 ? 'A' : 'B'
			this.setCookie('cmm_journey', journey, { secure: true, path: '/', sameSite: 'None' })
		}
		this.#booking.journey = journey as Journeys

		this.#render = () => {}
		this.#hasRender = false

		if (render) {
			this.#render = render
			this.#hasRender = true
		}

		if (!this.#booking.locator) this.#generateLocator()
		if (this.#booking.price === 0) this.updatePrice()

		if (
			this.#booking.date !== '' &&
			((this.#booking.shiftId !== 0 && new Date(this.#booking.date).getTime() < Date.now()) ||
				(this.#booking.shiftId === 0 &&
					new Date(this.#booking.date).getTime() < new Date().setHours(0, 0, 0, 0)))
		) {
			this.unSelectDate()
		}

		if (this.#useLocalStorage) save('sessionStorage', 'booking-data', this.#booking)
	}

	setRender(callback: () => void) {
		this.#render = callback
		this.#hasRender = true
	}

	/* ----------------------------------------------------------------
    |  RAW GETTERS
    ---------------------------------------------------------------- */
	get venueId() {
		return this.#booking.venueId
	}
	get hasRender() {
		return this.#hasRender
	}
	get price() {
		return this.#booking.price
	}
	get journey() {
		return this.#booking.journey
	}
	get locator() {
		return this.#booking.locator
	}
	get step() {
		return this.#booking.step
	}
	get language() {
		return this.#booking.language
	}
	get date() {
		return this.#booking.date
	}
	get shiftId() {
		return this.#booking.shiftId
	}
	get eventId() {
		return this.#booking.eventId
	}
	get children() {
		return this.#booking.children
	}
	get pax() {
		return this.#booking.pax
	}
	get meals() {
		return this.#booking.meals
	}
	get tables() {
		return this.#booking.tables
	}
	get products() {
		return this.#booking.products
	}
	get name() {
		return this.#booking.name
	}
	get email() {
		return this.#booking.email
	}
	get phone() {
		return this.#booking.phone
	}
	get country() {
		return this.#booking.country
	}
	get notes() {
		return this.#booking.notes
	}
	get access() {
		return this.#booking.access
	}
	get acceptedTerms() {
		return this.#booking.acceptedTerms
	}
	get discountCode() {
		return this.#booking.discountCode
	}
	/* ----------------------------------------------------------------
    |  UTILITY GETTERS
    ---------------------------------------------------------------- */
	render() {
		this.#render()
	}
	getBookingDataByReference() {
		return this.#booking
	}
	getOccupiedZones() {
		return removeDuplicates(
			this.#booking.products
				.filter((product) => product.type === 'ticket')
				.map((ticket) => ticket.zone) as string[],
		) as SafeZone[]
	}
	getStringSummary() {
		const [date, time] = this.#booking.date.split('T')
		const adultTickets = this.getTickets({ category: 'adult' })
		const summaryObj = this.getBookingSummary()

		const dateStep = `${_('0ge_date___')}: ${date ? parseDate('Monday d November yyyy', date) : _('0ar_notse__')},`
		const shiftStep = `${_('0ge_shift__')}: ${time && !time.startsWith('0') ? time : _('0ar_notse__')},`
		const ticketsStep = `${_('0ar_ticke__')}: ${
			adultTickets.length === 0
				? _('0ar_notse__')
				: adultTickets.map((ticket) => `${ticket.qty} ${getZoneName('long', ticket.zone, this.journey)}`)
		} ${this.children > 0 ? `+${this.children} ${_('0ar_child__')}` : ''},`
		const products = `${_('0ar_produ__')}: ${
			summaryObj.products.fnb.items.length > 0
				? summaryObj.products.fnb.items.map((item) => `${item.qty} ${item.name}`)
				: _('0ar_notse__')
		},`
		const total = `${_('0ge_total__')}: ${parseNum('EUR', summaryObj.total)}`

		return `${_('0ar_summa__')}: ${dateStep} ${shiftStep} ${ticketsStep} ${products} ${total}`
	}
	getZonesOccupancy() {
		const tickets = this.getTickets()
		return tickets.reduce(
			(occupancies, ticket) => ({
				...occupancies,
				[ticket.zone]: occupancies[ticket.zone] + ticket.qty,
			}),
			{ zv: 0, za: 0, zb: 0, zc: 0, za2: 0 },
		)
	}
	getZoneOccupancy(zone: SafeZone) {
		return this.#booking.products
			.filter((product) => product.zone === zone && product.type === 'ticket')
			.reduce((occupancy, product) => occupancy + product.qty, 0)
	}
	getTable(id: number) {
		return this.#booking.tables.find((table) => table.id === id)
	}
	getTableIndex(id: number) {
		return this.#booking.tables.findIndex((table) => table.id === id)
	}
	getAdults() {
		return this.#booking.pax - this.#booking.children
	}
	getTickets(options?: { zone?: SafeZone; category?: ProductCategory }) {
		return this.#booking.products.filter(
			(product) =>
				product.type === 'ticket' &&
				((options?.zone && product.zone === options.zone) || !options?.zone) &&
				((options?.category && product.category === options?.category) || !options?.category),
		) as Ticket[]
	}
	getTicketQty(options?: { zone?: SafeZone; category?: ProductCategory }) {
		return this.#booking.products
			.filter(
				(product) =>
					product.type === 'ticket' &&
					((options?.zone && product.zone === options.zone) || !options?.zone) &&
					((options?.category && product.category === options?.category) || !options?.category),
			)
			.reduce((total, ticket) => (total += ticket.qty), 0)
	}
	getUnusuedSeats() {
		return this.#booking.tables.reduce((unusuedSeats, table) => {
			return (unusuedSeats += table.pax - table.occupied)
		}, 0)
	}
	getTicketByLayoutLocation(tableId: number, seat?: number) {
		return this.#booking.products.find((product) => {
			if (!product.layoutLocations || product.type !== 'ticket') return false

			return product.layoutLocations.find(([productTableId, productSeat]) => {
				if (tableId !== undefined && productTableId === tableId) {
					if (seat === productSeat || seat === undefined) return true
					return false
				}
				return false
			})
		})
	}
	getFnbs() {
		return this.#booking.products.filter((product) => product.type === 'fnb' && product.category === 'dinner')
	}
	getOthers() {
		return this.#booking.products.filter((product) => product.type === 'other')
	}
	getProduct(id: Id) {
		return this.#booking.products.find((product) => product.productId === id)
	}
	getProductIndex(id: Id) {
		return this.#booking.products.findIndex((product) => product.productId === id)
	}
	getProductQty(id: Id) {
		return this.#booking.products.find((product) => product.productId === id)?.qty ?? 0
	}
	getTotalAmount() {
		return this.#booking.products.reduce((total, product) => total + product.price * product.qty, 0)
	}
	getBookingContext() {
		return { date: this.date, shiftId: this.shiftId, zones: this.getOccupiedZones() } as BookingContext
	}
	getProductsSummary() {
		const initValue: ProductsSummary = {
			ticket: { price: 0, qty: 0, items: [] },
			fnb: { price: 0, qty: 0, items: [] },
			other: { price: 0, qty: 0, items: [] },
		}
		return this.#booking.products.reduce((summary, product) => {
			summary[product.type].price += product.price * product.qty
			summary[product.type].qty += product.qty
			summary[product.type].items.push({
				id: product.productId,
				qty: product.qty,
				price: product.price,
				name: product.name,
				originalPrice: product.originalPrice,
				discounts: product.discounts,
			})
			return summary
		}, initValue)
	}

	getBookingSummary() {
		return {
			total: this.getTotalAmount(),
			products: this.getProductsSummary(),
			context: this.getBookingContext(),
		} as BookingSummary
	}

	isPossibleToReorganizeSeats() {
		let usedSeats = this.#booking.pax
		let sortedTables = this.#booking.tables.sort((a, b) => b.pax - a.pax)
		let usedTables = 0

		for (let table of sortedTables) {
			usedTables += 1
			usedSeats -= table.pax
			if (usedSeats <= 0) break
		}
		return usedTables < this.#booking.tables.length
	}

	calcSurcharge(surcharge: Product) {
		const surchargeData: SurchargeData = {
			amount: 0,
			surcharges: [],
		}

		let sucharges = this.getProductQty(surcharge.id)

		if (this.#booking.pax >= 3) return surchargeData

		for (let table of this.#booking.tables) {
			let tableOccupancy = table.occupied / table.pax
			let offset = 0
			let tableSurcharge = { tableId: table.id, zone: table.zone, qty: 0 }

			while (tableOccupancy <= 0.5) {
				tableSurcharge.qty += 1
				offset += 1
				surchargeData.amount += surcharge.price
				tableOccupancy = (table.occupied + offset) / table.pax
			}

			for (let i = 0; i < sucharges; i++) {
				sucharges -= 1
				tableSurcharge.qty -= 1
				surchargeData.amount -= surcharge.price
				if (sucharges === 0) break
			}

			if (tableSurcharge.qty > 0) surchargeData.surcharges.push(tableSurcharge)
		}

		return surchargeData
	}

	applySurcharge(surchargeData: SurchargeData, surchargeProduct: Product): Render {
		for (let surcharge of surchargeData.surcharges) {
			for (let i = 0; i < surcharge.qty; i++) {
				this.handleProduct('add', surchargeProduct)
			}
		}

		return this.#setBooking({
			...this.#booking,
			icons: ['circle.lefthalf.fill'],
		})
	}

	removeSurcharge(surcharge: Product): Render {
		//remove all surcharges when editing a table
		//after clicking continue if surcharge is > 0 modal will pop up &
		//sucharge will be applied again
		const paidSurcharges = this.#booking.products.find((product) => product.productId === surcharge.id)?.qty ?? 0
		for (let i = 0; i < paidSurcharges; i++) {
			this.handleProduct('remove', surcharge)
		}

		return this.#setBooking({
			...this.#booking,
			icons: this.#booking.icons.filter((icon) => icon !== 'circle.lefthalf.fill'),
		})
	}
	/* ----------------------------------------------------------------------
     |  BOOKING SETTERS
     ---------------------------------------------------------------------- */
	#generateLocator() {
		const today = new Date()
		const str_available = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
		let locator =
			Array.from({ length: 5 }, () => str_available[Math.floor(Math.random() * str_available.length)]).join('') +
			`-${today.getFullYear().toString().substring(2)}${today.getMonth() + 1}${today.getDate()}`
		this.#booking.locator = locator
	}

	#setBooking(booking: BookingData, opts?: { autoRender: boolean }): Render {
		this.#booking = booking
		if (this.#useLocalStorage) save('sessionStorage', 'booking-data', booking)
		return { render: this.#render }
	}
	goOneStepForward(): Render {
		const current = BOOKING_STEPS_IN_ORDER.indexOf(this.#booking.step)
		const newStep = BOOKING_STEPS_IN_ORDER[current + 1] ?? this.#booking.step
		return this.#setBooking({
			...this.#booking,
			step: newStep,
		})
	}
	goOneStepBackward(): Render {
		const current = BOOKING_STEPS_IN_ORDER.indexOf(this.#booking.step)
		const newStep = BOOKING_STEPS_IN_ORDER[current - 1] ?? this.#booking.step
		return this.#setBooking({
			...this.#booking,
			step: newStep,
		})
	}
	setBookingStep(step: BookingStep): Render {
		return this.#setBooking({
			...this.#booking,
			step,
		})
	}
	selectDate(date: string): Render {
		const newBooking: BookingData = {
			...this.#booking,
			date: `${date.substring(0, 10)}T00:00:00`,
			step: 'shift',
			//----------------- reset
			children: 0,
			eventId: 0,
			pax: 0,
			shiftId: 0,
			tables: [],
			meals: 0,
			products: [],
		}
		return this.#setBooking(newBooking)
	}
	unSelectDate(): Render {
		return this.#setBooking({
			...this.#booking,
			date: '',
			step: 'date',
			children: 0,
			eventId: 0,
			pax: 0,
			shiftId: 0,
			tables: [],
			meals: 0,
			products: [],
		})
	}
	selectShift(shift: Shift): Render {
		const newBooking: BookingData = {
			...this.#booking,
			children: 0,
			pax: 0,
			meals: 0,
			step: 'tickets',
			tables: [],
			products: [],
			shiftId: shift.id,
			eventId: shift.eventId,
			date: shift.date ? shift.date.substring(0, 19) : `${this.#booking.date.split('T')[0]}T${shift.time}:00`,
		}
		return this.#setBooking(newBooking)
	}
	unselectShift(): Render {
		return this.#setBooking({
			...this.#booking,
			shiftId: 0,
			eventId: 0,
			date: `${this.#booking.date.split('T')[0]}T00:00:00`,
			step: 'shift',
			children: 0,
			pax: 0,
			tables: [],
			meals: 0,
			products: [],
		})
	}
	updatePrice(): Render {
		return this.#setBooking({
			...this.#booking,
			price: this.getTotalAmount(),
		})
	}
	unselectTickets(): Render {
		this.#setBooking({
			...this.#booking,
			pax: 0,
			children: 0,
			products: [],
			tables: [],
			meals: 0,
			step: 'tickets',
		})
		return this.updatePrice()
	}
	unselectProducts(): Render {
		this.#setBooking({
			...this.#booking,
			products: this.#booking.products.filter((product) => product.type === 'ticket'),
			meals: 0,
		})
		return this.updatePrice()
	}
	handleProduct(action: 'add' | 'remove', product: Product, layoutLocation?: LayoutLocation): Render {
		/* - this product is a child ticket, handle pax + childs count + product
		 * - this product is an adult ticket, handle pax count + product
		 * - this product has dinner category, handle meals count + product
		 * - this product is in PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX, handle qty + product */
		const initialProductQty = this.getProductQty(product.id)

		const itsQtyIsFixedToPax = PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX.includes(product.id)
		const itsQtyIsFixedToAdults = PRODUCTS_WICH_QTY_IS_FIXED_TO_ADULTS.includes(product.id)
		const itsQtyIsFixedToChildren = PRODUCTS_WICH_QTY_IS_FIXED_TO_CHILDREN.includes(product.id)

		let offset = itsQtyIsFixedToPax
			? this.#booking.pax
			: itsQtyIsFixedToAdults
				? this.#booking.pax - this.#booking.children
				: itsQtyIsFixedToChildren
					? this.#booking.children
					: 1

		if (action === 'remove') offset *= -1

		let pax = this.#booking.pax
		let children = this.#booking.children
		let meals = this.#booking.meals
		let qty = initialProductQty + offset
		let bookingProducts: BookingProduct[] = [...this.#booking.products]

		const newBookingProduct: BookingProduct = {
			productId: product.id,
			price: product.price,
			name: product.name,
			discounts: product.discounts,
			originalPrice: product.originalPrice,
			qty,
			zone: product.zone,
			category: product.category,
			type: product.type,
			layoutLocations: [],
		}

		if (layoutLocation) newBookingProduct.layoutLocations!.push(layoutLocation)

		if (product.type === 'ticket') pax += offset
		if (product.type === 'ticket' && product.category === 'children') children += offset
		if (product.category === 'dinner') meals += offset

		if (initialProductQty === 0) bookingProducts.push(newBookingProduct)
		else
			bookingProducts = bookingProducts
				.map((bookingProduct) => {
					if (bookingProduct.productId !== product.id) return bookingProduct

					if (action === 'add' && product.type === 'ticket' && layoutLocation) {
						//add the new location
						newBookingProduct.layoutLocations = [...bookingProduct.layoutLocations!, layoutLocation]
					} else if (action === 'remove' && product.type === 'ticket' && layoutLocation) {
						//removes the location
						newBookingProduct.layoutLocations = bookingProduct.layoutLocations?.filter(
							([tableId, seat]) => tableId !== layoutLocation[0] || seat !== layoutLocation[1],
						)
					}

					return newBookingProduct
				})
				.filter((product) => product.qty > 0)

		if (action === 'remove' && product.type === 'ticket') {
			let temp: BookingProduct[] = []
			for (const bookingProduct of bookingProducts) {
				if (!bookingProduct.layoutLocations) continue
				if (bookingProduct.type !== 'ticket') continue
				if (bookingProduct.category !== 'children') {
					//filter by adults tickets
					temp.push(bookingProduct)
					continue
				}
				pax -= bookingProduct.qty
				children -= bookingProduct.qty

				console.log(pax)
				// handleTable
				for (const [tableId, seat] of bookingProduct.layoutLocations) {
					const table = this.getTable(tableId)
					if (!table) continue

					this.handleTable({
						...table,
						occupied: Math.max(0, table.occupied - 1),
						seats: table.seats.filter((tableSeat) => tableSeat !== seat),
					})
				}
			}
			bookingProducts = temp
		}

		this.#setBooking({
			...this.#booking,
			products: bookingProducts,
			meals,
			pax: Math.max(0, pax),
			children,
		})
		return this.updatePrice()
	}

	handleProductWithOutTables(product: Product, newQty: number) {
		let meals = this.meals,
			children = this.children,
			pax = this.pax

		const productInBooking = this.getProduct(product.id)
		const diff = newQty - (productInBooking?.qty || 0)

		if (productInBooking)
			this.#booking.products = this.products
				.map((p) => ({ ...p, qty: p.productId === product.id ? newQty : p.qty }))
				.filter((p) => p.qty > 0)
		else
			this.#booking.products.push({
				productId: product.id,
				zone: product.zone,
				type: product.type,
				category: product.category,
				price: product.price,
				name: product.name,
				originalPrice: product.originalPrice,
				discounts: product.discounts,
				qty: newQty,
			})

		if (product.type === 'ticket') {
			pax += diff
			if (product.category === 'children') {
				children += diff
			}
		}
		if (product.type === 'fnb') {
			meals += diff
		}

		this.#booking.products = this.#booking.products
			.map((p) => {
				if (PRODUCTS_WICH_QTY_IS_FIXED_TO_PAX.includes(p.productId)) {
					return { ...p, qty: pax }
				}
				return p
			})
			.filter((p) => p.qty > 0)

		this.#setBooking({
			...this.#booking,
			meals,
			pax,
			children,
		})
		return this.updatePrice()
	}

	handleTable(table: BookingTable): Render {
		/* - booking has table and this new object has seats, updates
		 * - booking has table and this new object do not have seats, deletes
		 * - booking do not has table, adds */

		let tableInBooking = this.getTable(table.id)
		let bookingTables: BookingTable[] = [...this.#booking.tables]

		if (tableInBooking)
			bookingTables = bookingTables
				.map((bookingTable) => {
					if (bookingTable.id === table.id) return table
					return bookingTable
				})
				.filter((bookingTable) => bookingTable.occupied > 0)
		else bookingTables.push(table)

		return this.#setBooking({ ...this.#booking, tables: bookingTables })
	}

	setPersonalDetail(key: PersonalDetailKey, value: string): Render {
		if (key === 'prefix' || key === 'number')
			this.#setBooking(
				{ ...this.#booking, phone: { ...this.#booking.phone, [key]: value } },
				{ autoRender: false },
			)
		else this.#setBooking({ ...this.#booking, [key]: value }, { autoRender: false })

		return { render: this.#render }
	}
	setAccess(access: 0 | 2) {
		return this.#setBooking({ ...this.#booking, access })
	}
	setAcceptedTerms(acceptedTerms: boolean): Render {
		return this.#setBooking({ ...this.#booking, acceptedTerms })
	}
	/* ----------------------------------------------------------------------
     |  VALIDATIONS
     ---------------------------------------------------------------------- */
	validatePersonalDetail(options: ValidatePersonalDetailOptions) {
		let newErrors: ErrorInfo[] = []
		const field = options.field
		const checkAs = options.checkFieldAs
		const rawName = field === 'name' || field === undefined ? (checkAs ?? this.#booking.name) : this.#booking.name
		const nameValue = typeof rawName === 'string' ? rawName.trim() : ''
		const nameParts = nameValue.split(/\s+/).filter(Boolean)

		if (options.shift === undefined) {
			newErrors.push({
				code: 'ERR_COULD_NOT_VALIDATE',
				message: '0ge_loadi__',
				type: 'load',
				associatedTo: 'global',
			})
			return newErrors
		}

		//check for empty values
		if ((field === 'name' || field === undefined) && nameValue === '')
			newErrors.push({
				code: 'ERR_FIELD_REQUIRED',
				message: '0er_field__',
				type: 'required',
				associatedTo: 'name',
			})
		if ((field === 'name' || field === undefined) && (nameParts.length < 2 || /\d/.test(nameValue)))
			newErrors.push({ code: 'ERR_INVALID', message: '0er_namea__', type: 'grammar', associatedTo: 'name' })
		if ((field === 'name' || field === undefined) && ((checkAs ?? this.#booking.name) as string).length > 255)
			newErrors.push({
				code: 'ERR_MAX_LENGTH_EXCEEDED',
				message: '0er_thefi__',
				type: 'length',
				associatedTo: 'name',
			})

		if ((field === 'email' || field === undefined) && (checkAs ?? this.#booking.email) === '')
			newErrors.push({
				code: 'ERR_FIELD_REQUIRED',
				message: '0er_field__',
				type: 'required',
				associatedTo: 'email',
			})
		if ((field === 'email' || field === undefined) && ((checkAs ?? this.#booking.email) as string).length > 255)
			newErrors.push({
				code: 'ERR_MAX_LENGTH_EXCEEDED',
				message: '0er_thefi__',
				type: 'length',
				associatedTo: 'email',
			})
		if (
			(field === 'email' || field === undefined) &&
			!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test((checkAs ?? this.#booking.email) as string)
		)
			newErrors.push({ code: 'ERR_INVALID', message: '0er_inval_e', type: 'grammar', associatedTo: 'email' })

		if (
			options.shift.phoneRequired &&
			(field === 'number' || field === undefined) &&
			(checkAs ?? this.#booking.phone.number) === ''
		)
			newErrors.push({
				code: 'ERR_FIELD_REQUIRED',
				message: '0er_field__',
				type: 'required',
				associatedTo: 'number',
			})
		if (
			options.shift.phoneRequired &&
			(field === 'number' || field === undefined) &&
			!/^[+\d\s]+$/.test((checkAs ?? this.#booking.phone.number) as string)
		)
			newErrors.push({ code: 'ERR_INVALID', message: '0er_inval_n', type: 'grammar', associatedTo: 'number' })

		if (
			SHOW_COUNTRY_FIELD &&
			(field === 'country' || field === undefined) &&
			(checkAs ?? this.#booking.country) === ''
		)
			newErrors.push({
				code: 'ERR_FIELD_REQUIRED',
				message: '0er_field__',
				type: 'required',
				associatedTo: 'country',
			})

		if ((field === 'acceptedTerms' || field === undefined) && !(checkAs ?? this.#booking.acceptedTerms))
			newErrors.push({
				code: 'ERR_FIELD_REQUIRED',
				message: '0er_field__',
				type: 'required',
				associatedTo: 'accepted-terms',
			})

		return newErrors
	}

	hasTableSelection() {
		// only 1911 has table selection
		return this.venueId === 2
	}
}
